import React, { useEffect } from 'react'

/**
 * Hardens two scroll-related behaviours from the source site without
 * modifying its scripts:
 *
 *  1.  Scroll reveal. Two jobs:
 *      (a) a per-item cascade, so the contents of a section arrive one
 *          after another as it scrolls into view rather than the whole
 *          block appearing at once. The source site wrote a `--d` per
 *          child for this but nothing consumed it (see the note beside
 *          [data-reveal-staggered] in Pentabay.html), so no stagger had
 *          ever actually rendered.
 *      (b) a safety net for `[data-reveal]` sections the observer can
 *          miss - e.g. a section already scrolled past when you jump to
 *          the footer - which would otherwise sit at opacity 0 forever.
 *
 *  2.  The pinned horizontal product track (`#prodTrack`) lerps
 *      `--track-x` slowly (ease 0.08). When the user fast-scrolls UP
 *      into `#products` from below, the track stays parked at maxX
 *      while the visible area is empty.
 *      We add a higher-priority CSS variable `--pb-track-x` that
 *      a custom rAF loop drives with a faster ease, so the cards are
 *      always close to the scroll position.
 *
 *  Both are additive: we never remove the source's own classes or
 *  styles, we just supplement them.
 */
export default function RevealFixer() {
  useEffect(() => {
    // ---------- 1. Reveal: stagger + safety net ---------------------
    const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    /* Per-item delay, raised from 70ms to 130ms so the cascade is clearly a
       cascade rather than a soft blur of near-simultaneous arrivals - you can
       now count the cards in.
       The cap moved with it (490 -> 910, i.e. still seven distinct steps before
       items start sharing a delay). It has to move: leaving it at 490 with a
       130ms step would give only three distinct arrivals and clamp everything
       after, which is a worse cascade than the 70ms one it replaced. The cap
       still exists for its original reason - a 15-item section (#why-us) would
       otherwise run past 1.9s and finish after the reader has moved on. */
    /* 2026-08 intensity pass: 130 -> 150 so the cascade reads as a visible
       sequence rather than a near-simultaneous blur, and the cap moves with it
       (7 items x 150). Anything that derives a wait from this cascade has to
       move too - FlowDraw.jsx delays its timeline past it and carries the
       arithmetic in a comment. */
    const STEP_MS = 150
    const MAX_DELAY_MS = 1050

    /* Containers whose children must NOT be animated individually. The marquees
       and carousels clone, reorder or transform their own children
       (ClientCarousel.jsx, FrameworksCarousel.jsx, the .prod-track rail), so a
       per-child opacity/transform of ours would either land on clones that did
       not exist when we ran, or fight the transform they animate every frame.
       Each is revealed as one unit instead. */
    const OPAQUE_CONTAINERS =
      '[data-marquee],[data-client-carousel],[data-fw-carousel],.cc-viewport,.cc-track,.prod-track,.fw-panel,.fw-stage,.client-carousel'

    /* Subtrees that already own a purpose-built reveal, which ours must not
       touch AT ALL - not even as a single unit.
       #industries-serve is the live case: .ind-grid[data-ind-anim="1"] .ind-card
       carries its own hidden state, its own --i stagger from the markup and a
       .is-in trigger. Running our cascade over the top of that double-animates
       eleven cards - and it is the case that exposed the specificity problem in
       the first place, when the earlier CSS-class approach lost to
       .ind-grid[data-ind-anim="1"] .ind-card (0,4,0) and stranded all eleven at
       opacity 0. Anything with its own mechanism goes here. */
    const SELF_ANIMATED = '[data-ind-anim],.ind-grid,[data-rv]'

    /* Directional reveal variants, opted into per element with data-rv in the
       markup. The generic cascade below moves everything the same way (up), so
       a long page of sections all rising by 20px reads as one repeated gesture.
       These give a few sections a different entrance: left-aligned heading
       blocks slide in from the left, and the two "centrepiece" blocks (the
       product carousel and the closing CTA) scale up.

       They are played with WAAPI for the same reason playStagger is, and the
       reason is worth restating because it has bitten this file twice: a
       CSS-class approach (.reveal / .revealed with an opacity:0 hidden state)
       loses the specificity fight to the cards' and panels' own rules and
       strands them at opacity 0 - that is exactly how eleven .ind-card tiles
       ended up invisible. A WAAPI animation composites above every declarative
       style, so specificity is irrelevant, AND it needs no hidden state in the
       stylesheet at all: if this file never runs, the elements are simply
       visible. There is no failure mode that leaves the page blank.

       data-rv is also in SELF_ANIMATED above, so the generic per-item cascade
       skips these subtrees instead of double-animating them. */
    /* 2026-08 intensity pass: -40px -> -104px and 0.94 -> 0.86, played on an
       overshoot curve (see playVariants). The scale variant therefore passes
       ~1.03 before settling on 1, which is the "landed" read; both keyframe sets
       still END at transform:none, which is where [data-reveal].in lands, so
       releasing the animation still cannot cause a jump. */
    const RV_KEYFRAMES = {
      left: [
        { opacity: 0, transform: 'translateX(-104px)' },
        { opacity: 1, transform: 'none' },
      ],
      scale: [
        { opacity: 0, transform: 'scale(0.86)' },
        { opacity: 1, transform: 'none' },
      ],
    }

    /* The CSS equivalent of GSAP's back.out(~1.6). WAAPI takes a cubic-bezier,
       not a GSAP ease name, so this layer and the GSAP components are matched by
       CURVE rather than by string. Used for both players here because both move
       a discrete block into place, where passing the target and settling back is
       exactly the intended read.
       NOT usable on anything whose end value cannot be exceeded: a y1 > 1
       control point on opacity, or on a 0->100% draw, just clamps at the top and
       reads as a plateau. Those use cubic-bezier(.16,1,.3,1) instead. */
    const EASE_OVERSHOOT = 'cubic-bezier(.34,1.56,.64,1)'

    const isSelfAnimated = (el) => el.matches(SELF_ANIMATED) || !!el.closest(SELF_ANIMATED)

    function usableChildren(el) {
      return Array.from(el.children).filter((c) => {
        if (c.getAttribute('aria-hidden') === 'true') return false
        // Decorative full-bleed art layers (.trust-art / .whyus-art) are
        // absolutely positioned; they are backdrops, not content to stagger.
        const cs = getComputedStyle(c)
        if (cs.position === 'absolute' || cs.position === 'fixed') return false
        if (cs.display === 'none') return false
        if (!c.offsetHeight) return false
        return true
      })
    }

    /* Walk down at most two levels looking for a row of peers to stagger.
       - a grid/flex box with 2+ visible children => stagger those children
       - a pass-through wrapper => descend
       - anything else => the element itself is one item
       Two levels is enough for this markup (section > wrapper > card grid) and
       shallow enough that it can't turn a whole section into 40 items. */
    function collectItems(el, out, depth) {
      // Owns its own reveal - leave it entirely alone.
      if (isSelfAnimated(el)) return
      if (el.matches(OPAQUE_CONTAINERS) || el.closest(OPAQUE_CONTAINERS)) {
        out.push(el)
        return
      }
      const kids = usableChildren(el)
      const cs = getComputedStyle(el)
      /* A row only staggers if its peers are card-like. The 48px floor is what
         keeps inline runs out of it - the COVERAGE legend in #canada is a flex
         box of five ~18px spans, and cascading those made a caption look like it
         was dealing itself out. Card grids clear 48px comfortably. */
      const cardLike = kids.filter((k) => k.offsetHeight >= 48)
      const isRow =
        (cs.display === 'grid' || cs.display === 'flex' || cs.display === 'inline-flex') &&
        kids.length >= 2 &&
        cardLike.length >= 2
      if (isRow) {
        // Filter here too: a row can contain a self-animated child, and spreading
        // kids straight into the list would bypass the guard at the top.
        for (const k of kids) if (!isSelfAnimated(k)) out.push(k)
        return
      }
      if (depth < 2 && kids.length) {
        for (const k of kids) collectItems(k, out, depth + 1)
        return
      }
      out.push(el)
    }

    /* Play the cascade with the Web Animations API rather than a CSS class.
       See the long note beside [data-reveal-staggered] in Pentabay.html: the
       items are cards that already declare their own `transition` at higher
       specificity, so a utility class carrying the reveal transition got
       overridden and the cards jumped. A WAAPI animation composites above every
       declarative style, so specificity is irrelevant.

       fill: 'backwards' is load-bearing - it applies the from-keyframe during
       the delay, so an item with a 490ms delay is held invisible from frame one
       instead of being painted, then yanked back to hidden.
       No fill afterwards, on purpose: once it finishes the element is released
       to its own styles, so each card's hover lift still works. */
    /* Play the directional variants for a section that has just revealed.
       Covers both shapes: the variant can be a DESCENDANT of the [data-reveal]
       section (every heading block), or the [data-reveal] element itself (the
       #contact CTA, where the reveal target and the variant target are the same
       div). Both keyframe sets end at transform:none, which is also where
       [data-reveal].in lands, so releasing the animation cannot cause a jump. */
    function playVariants(section) {
      if (REDUCE) return
      const list = []
      if (section.hasAttribute('data-rv')) list.push(section)
      section.querySelectorAll('[data-rv]').forEach((el) => list.push(el))
      for (const el of list) {
        if (el.dataset.rvVariantPlayed === '1') continue
        const kf = RV_KEYFRAMES[el.getAttribute('data-rv')]
        if (!kf) continue
        el.dataset.rvVariantPlayed = '1'
        el.animate(kf, {
          // 620 -> 480: a tighter arrival reads as more energetic than a long float.
          duration: 480,
          easing: EASE_OVERSHOOT,
          fill: 'backwards',
        })
      }
    }

    function playStagger(section) {
      if (REDUCE) return
      if (section.dataset.rvPlayed === '1') return
      /* The section itself carries a variant (#contact), so its children must
         not also be dealt out one by one on top of the scale. */
      if (isSelfAnimated(section)) return
      const items = []
      for (const kid of usableChildren(section)) collectItems(kid, items, 1)
      // One item is not a cascade - leave the section's own block motion alone.
      if (items.length < 2) return
      // An absurd count means the walk found something list-like we did not
      // anticipate; fall back to the section's own motion rather than animating
      // dozens of nodes. 30 comfortably covers the biggest real section
      // (#why-us resolves to 15).
      if (items.length > 30) return

      section.dataset.rvPlayed = '1'
      section.setAttribute('data-reveal-staggered', '')
      items.forEach((el, i) => {
        el.animate(
          /* 112px / 560ms, raised from 44px / 780ms (itself raised from
             20px / 520ms). This is the distance that actually matters for most
             of the site: it is the reveal for every cascaded section on every
             route, and for those the section's own CSS travel is zeroed out by
             [data-reveal][data-reveal-staggered], so these keyframes ARE the
             visible motion. Bumping only the CSS would leave the majority of the
             page animating exactly as subtly as before.
             Farther AND faster on an overshoot curve: the item passes its resting
             position by ~18px and settles, which is what makes the arrival
             legible at a glance instead of a quiet drift. */
          [
            { opacity: 0, transform: 'translateY(112px)' },
            { opacity: 1, transform: 'none' },
          ],
          {
            duration: 560,
            delay: Math.min(i * STEP_MS, MAX_DELAY_MS),
            easing: EASE_OVERSHOOT,
            fill: 'backwards',
          }
        )
      })
    }

    function reveal(el) {
      revealIO.unobserve(el)
      if (el.classList.contains('in')) return
      /* Order matters: start the animations in the SAME task as the class flip.
         fill:'backwards' then has the from-state in place for the first painted
         frame, so nothing flashes at full opacity before dropping to hidden. */
      if (el.hasAttribute('data-reveal')) {
        playVariants(el)
        playStagger(el)
      }
      el.classList.add('in')
    }

    /* rootMargin matches the source observer's -15% bottom inset on purpose:
       the section should start arriving once it is genuinely in view, so the
       cascade happens while the reader is looking at it. */
    const revealIO = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) reveal(e.target)
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.01 }
    )

    function scanReveals() {
      document
        .querySelectorAll('[data-reveal]:not(.in), [data-reveal-line]:not(.in)')
        .forEach((el) => revealIO.observe(el))
    }
    scanReveals()

    /* Safety net for what the observer can miss: a section that is already
       scrolled PAST (fast jump to the footer, or an in-page anchor landing
       below it) would otherwise sit at opacity 0 forever when you scroll back.
       Deliberately NOT a lookahead. This used to fire on anything within 200px
       of the viewport in either direction, which meant almost every section was
       revealed BEFORE it came on screen - the animation finished offscreen and
       the page appeared to have no reveal at all. The trigger line now matches
       the observer's (85% of the viewport height). */
    function eagerRevealOnscreen() {
      const vh = window.innerHeight
      document
        .querySelectorAll('[data-reveal]:not(.in), [data-reveal-line]:not(.in)')
        .forEach((el) => {
          const r = el.getBoundingClientRect()
          const wellInView = r.top < vh * 0.85 && r.bottom > 0
          const alreadyScrolledPast = r.bottom <= 0
          if (wellInView || alreadyScrolledPast) reveal(el)
        })
    }

    const mo = new MutationObserver(() => {
      scanReveals()
      eagerRevealOnscreen()
    })
    mo.observe(document.body, { childList: true, subtree: true })

    /* rAF-throttled, same pattern as onTrackScroll below: a bare 'scroll'
       listener can fire many times per animation frame on a trackpad or a
       high-refresh display, and eagerRevealOnscreen() forces a layout read
       (getBoundingClientRect) per not-yet-revealed element on every call.
       Capping it at once per frame doesn't change what it does - the
       IntersectionObserver above is still the primary mechanism and this
       stays a safety net for elements scrolled past before observe() saw
       them - it just stops that read from running more often than the
       screen can show a new frame anyway. */
    let eagerRaf = 0
    const onScroll = () => {
      if (eagerRaf) return
      eagerRaf = requestAnimationFrame(() => {
        eagerRaf = 0
        eagerRevealOnscreen()
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    eagerRevealOnscreen()

    // ---------- 2. Product track faster lerp ------------------------
    // Inject a higher-priority transform that uses our own variable;
    // the source's `--track-x` still updates but ours wins.
    const styleEl = document.createElement('style')
    styleEl.setAttribute('data-pb-revealfixer', '')
    styleEl.textContent = `
      @media (min-width: 1024px) {
        .pin-h-pin .prod-track {
          transform: translate3d(var(--pb-track-x, var(--track-x, 0px)), 0, 0) !important;
        }
      }
    `
    document.head.appendChild(styleEl)

    let trackTargetX = 0
    let trackCurrentX = 0
    let trackRaf = 0
    let trackLerping = false

    function trackEls() {
      const section = document.getElementById('products')
      if (!section) return null
      const track = document.getElementById('prodTrack')
      if (!track) return null
      const wrap = section.querySelector('.pin-h-track-wrap')
      if (!wrap) return null
      return { section, track, wrap }
    }

    function trackTick() {
      const els = trackEls()
      if (!els) { trackLerping = false; return }
      const { track } = els
      // Faster ease: 0.22 (vs source 0.08). Snappier but still smooth.
      trackCurrentX += (trackTargetX - trackCurrentX) * 0.22
      if (Math.abs(trackTargetX - trackCurrentX) < 0.4) {
        trackCurrentX = trackTargetX
        track.style.setProperty('--pb-track-x', trackCurrentX + 'px')
        trackLerping = false
        return
      }
      track.style.setProperty('--pb-track-x', trackCurrentX + 'px')
      trackRaf = requestAnimationFrame(trackTick)
    }

    function trackRecompute() {
      const els = trackEls()
      if (!els) return
      const { section, track, wrap } = els
      if (window.innerWidth < 1024) {
        track.style.removeProperty('--pb-track-x')
        return
      }
      const rect = section.getBoundingClientRect()
      const total = section.offsetHeight - window.innerHeight
      let progress = total > 0 ? -rect.top / total : 0
      progress = Math.max(0, Math.min(1, progress))
      const wrapW = wrap.clientWidth
      const maxX = Math.max(0, track.scrollWidth - wrapW)
      trackTargetX = -progress * maxX

      // If the section is just (re-)entering view from below, snap
      // currentX close to targetX so the cards appear immediately
      // instead of lerping in from offscreen.
      const enteringFromBelow = rect.top > -window.innerHeight && rect.top < window.innerHeight
      if (enteringFromBelow && Math.abs(trackTargetX - trackCurrentX) > wrapW * 0.5) {
        // Half-snap: jump 80% of the way so we still get a touch of motion.
        trackCurrentX = trackTargetX + (trackCurrentX - trackTargetX) * 0.2
      }

      if (!trackLerping) {
        trackLerping = true
        trackRaf = requestAnimationFrame(trackTick)
      }
    }

    let scrollRaf = 0
    const onTrackScroll = () => {
      if (scrollRaf) return
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0
        trackRecompute()
      })
    }
    window.addEventListener('scroll', onTrackScroll, { passive: true })
    window.addEventListener('resize', onTrackScroll)

    // Initial layout pass; the source script may not have run yet on
    // first paint, so retry until the products section exists.
    let initialAttempts = 0
    function initialKick() {
      const els = trackEls()
      if (els) { trackRecompute() }
      else if (initialAttempts++ < 80) setTimeout(initialKick, 100)
    }
    initialKick()

    return () => {
      revealIO.disconnect()
      mo.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('scroll', onTrackScroll)
      window.removeEventListener('resize', onTrackScroll)
      if (trackRaf) cancelAnimationFrame(trackRaf)
      if (scrollRaf) cancelAnimationFrame(scrollRaf)
      if (eagerRaf) cancelAnimationFrame(eagerRaf)
      styleEl.remove()
    }
  }, [])

  return null
}
